import {
  agentOrderTabs,
  agentSurfaces,
  type AgentOrderTab,
  type AgentSurface,
} from '@hungryroot/rest';
import { getNavTabFromPathname } from '@hungryroot/segment';
import {
  OrdersTab,
  parseTabFromSearch,
} from '../../../components/OrdersPage/types';

export interface AgentSurfaceFields {
  surface?: AgentSurface;
  tab?: AgentOrderTab;
}

/**
 * The Orders page has four sub-tabs; sodapop's contract has three. `Cart` is
 * its `current`. `OnTheWay` has no counterpart, so it is left off the wire and
 * sodapop falls back to the `current` set on its own.
 */
const ORDERS_TAB_TO_AGENT_TAB: Record<OrdersTab, AgentOrderTab | undefined> = {
  [OrdersTab.Cart]: agentOrderTabs.current,
  [OrdersTab.OnTheWay]: undefined,
  [OrdersTab.Upcoming]: agentOrderTabs.upcoming,
  [OrdersTab.Delivered]: agentOrderTabs.delivered,
};

/**
 * Surface and Orders sub-tab for a location, used to pick the chat's starter
 * prompts. `surface` is undefined on routes that belong to no top-level tab —
 * sodapop then serves its generic set.
 *
 * The nav-tab mapping is shared with analytics (`getNavTabFromPathname`) so the
 * two cannot disagree about what counts as Home, Search, Cookbook or Orders.
 */
export function resolveAgentSurfaceFields({
  pathname,
  search,
}: {
  pathname: string;
  search: string;
}): AgentSurfaceFields {
  const navTab = getNavTabFromPathname(pathname, search);
  if (navTab === null) {
    return {};
  }
  if (navTab !== agentSurfaces.orders) {
    return { surface: navTab };
  }
  return {
    surface: agentSurfaces.orders,
    tab: ORDERS_TAB_TO_AGENT_TAB[parseTabFromSearch(search)],
  };
}

/**
 * `resolveAgentSurfaceFields` for the current `window.location`; empty during
 * SSR.
 */
export function getCurrentAgentSurfaceFields(): AgentSurfaceFields {
  if (typeof window === 'undefined') {
    return {};
  }
  return resolveAgentSurfaceFields(window.location);
}
